import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  FilterOptionsState,
  TextField,
} from '@mui/material';
import { Field } from 'mobx-react-form';
import { observer } from 'mobx-react-lite';

export type AutocompleteOption = {
  inputValue?: string;
  title: string;
};

type ComboFieldProps = {
  field: Field;
  options: AutocompleteOption[];
  TextFieldProps?: any;
  AutoCompleteProps?: Partial<AutocompleteProps<AutocompleteOption, false, false, true>>;
  filter: (options: AutocompleteOption[], state: FilterOptionsState<AutocompleteOption>) => AutocompleteOption[];
} & Partial<Pick<AutocompleteRenderInputParams, 'inputProps'>>;

const ComboField = observer(
  ({ field, options, TextFieldProps, inputProps, AutoCompleteProps, filter }: ComboFieldProps) => {
    const handleBlur = () => {
      field.validate({ showErrors: true });
    };

    return (
      <Autocomplete
        {...AutoCompleteProps}
        value={field.value}
        onBlur={handleBlur}
        onChange={(event, newValue) => {
          if (typeof newValue === 'string') {
            field.onChange({
              title: newValue,
            });
          } else if (newValue?.inputValue) {
            field.onChange({
              title: newValue.inputValue,
            });
          } else {
            field.onChange(newValue);
          }

          field.validate({ showErrors: true });
        }}
        filterOptions={(options, state) => {
          const filtered = filter(options, state);

          const { inputValue } = state;
          // Suggest the creation of a new value
          const isExisting = options.some((option) => inputValue === option.title);
          if (inputValue !== '' && !isExisting) {
            filtered.push({
              inputValue,
              title: `Add "${inputValue}"`,
            });
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={options}
        getOptionLabel={(option) => {
          // Value selected with enter, right from the input
          if (typeof option === 'string') {
            return option;
          }
          // Add "xxx" option created dynamically
          if (option.inputValue) {
            return option.inputValue;
          }
          // Regular option
          return option.title;
        }}
        renderOption={(props, option) => <li {...props}>{option.title}</li>}
        freeSolo
        renderInput={(params) => (
          <TextField
            {...params}
            error={!!field.error}
            helperText={field.error}
            {...field.bind()}
            {...TextFieldProps}
            inputProps={{
              ...params.inputProps,
              ...inputProps,
            }}
            onBlur={(e) => {
              // @ts-ignore
              params.inputProps.onBlur?.(e);
              handleBlur();
            }}
          />
        )}
      />
    );
  }
);

export { ComboField };
